import { useState, useMemo } from 'react'
import type { TenantSummary } from '../../services/api'
import { validateRUT, formatRUT, normalizeRUT } from '../../lib/fiscal/uruguay'

interface BillingEditModalProps {
  tenant: TenantSummary
  onSave: (tenantId: string, data: BillingFormData) => Promise<void>
  onClose: () => void
}

interface BillingFormData {
  legalName: string
  taxId: string
  billingAddress: string
  billingPostalCode: string
  billingCountry: string
  billingEmails: string[]
  paymentTerms: 'net_15' | 'net_30' | 'net_45' | 'net_60'
  billingDay: number
  prorationEnabled: boolean
}

const PAYMENT_TERMS_OPTIONS: Array<{ value: BillingFormData['paymentTerms']; label: string; hint: string }> = [
  { value: 'net_15', label: 'Net 15', hint: 'Vence 15 días después de emitida' },
  { value: 'net_30', label: 'Net 30', hint: 'Vence 30 días después (estándar)' },
  { value: 'net_45', label: 'Net 45', hint: 'Vence 45 días después' },
  { value: 'net_60', label: 'Net 60', hint: 'Vence 60 días después' },
]

// LATAM + ES + US coverage. When a country needs client-side tax_id
// validation, add it to TAX_ID_VALIDATORS below.
const countries = [
  { code: 'UY', name: 'Uruguay' },
  { code: 'CL', name: 'Chile' },
  { code: 'AR', name: 'Argentina' },
  { code: 'MX', name: 'México' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Perú' },
  { code: 'ES', name: 'España' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'BR', name: 'Brasil' },
]

/**
 * Per-country tax-id validators. Only Uruguay is enforced today (Sprint 2
 * scope). Other countries pass through — the field still lives in the DB
 * as free text; when Jarix starts billing from a new country we add its
 * validator here.
 */
function validateTaxId(country: string, raw: string): { ok: boolean; message?: string; normalized?: string; formatted?: string } {
  if (country === 'UY') {
    const v = validateRUT(raw)
    if (!v.valid) return { ok: false, message: v.message, normalized: v.normalized }
    return { ok: true, normalized: v.normalized, formatted: formatRUT(v.normalized) }
  }
  // Minimal non-empty check for every other country.
  if (!raw || raw.trim().length === 0) {
    return { ok: false, message: 'El identificador fiscal es obligatorio' }
  }
  return { ok: true, normalized: raw.trim() }
}

export default function BillingEditModal({ tenant, onSave, onClose }: BillingEditModalProps) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState<BillingFormData>({
    legalName: tenant.billing?.legalName || '',
    taxId: tenant.billing?.taxId || '',
    billingAddress: tenant.billing?.billingAddress || '',
    billingPostalCode: tenant.billing?.billingPostalCode || '',
    billingCountry: tenant.billing?.billingCountry || 'UY',
    billingEmails: tenant.billing?.billingEmails || [],
    paymentTerms: (tenant.billing?.paymentTerms as BillingFormData['paymentTerms']) || 'net_30',
    billingDay: tenant.billing?.billingDay ?? 15,
    prorationEnabled: !!tenant.billing?.prorationEnabled,
  })

  const [newEmail, setNewEmail] = useState('')

  // Live inline tax-id validation. Memoized so we don't re-run the
  // (cheap but noisy) regex / mod-11 check on every unrelated render.
  const taxIdStatus = useMemo(
    () => validateTaxId(formData.billingCountry, formData.taxId),
    [formData.billingCountry, formData.taxId],
  )
  const taxIdTouched = formData.taxId.trim().length > 0

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase()
    if (email && !formData.billingEmails.includes(email) && email.includes('@')) {
      setFormData(prev => ({
        ...prev,
        billingEmails: [...prev.billingEmails, email],
      }))
      setNewEmail('')
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      billingEmails: prev.billingEmails.filter(e => e !== emailToRemove),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.legalName.trim()) {
      setError('La razón social es obligatoria')
      return
    }
    if (!taxIdStatus.ok) {
      setError(taxIdStatus.message || 'El identificador fiscal no es válido')
      return
    }
    if (formData.billingEmails.length === 0) {
      setError('Agrega al menos un correo de facturación')
      return
    }
    if (!Number.isInteger(formData.billingDay) || formData.billingDay < 1 || formData.billingDay > 28) {
      setError('El día de corte debe estar entre 1 y 28')
      return
    }

    try {
      setSaving(true)
      // Persist the normalized tax id (digits-only for Uruguay) so
      // NetSuite and the Finanzas CSV always get a canonical form,
      // regardless of how the user typed it.
      await onSave(tenant.id, {
        ...formData,
        taxId: taxIdStatus.normalized ?? formData.taxId,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Datos de Facturación</h2>
              <p className="text-gray-400 text-sm mt-1">
                <span
                  className="inline-block w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: tenant.primaryColor }}
                />
                {tenant.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700"
              aria-label="Cerrar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Legal Name */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">
              Razón Social <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.legalName}
              onChange={(e) => setFormData(prev => ({ ...prev, legalName: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-jarix-orange"
              placeholder="Nombre legal de la empresa"
            />
            <p className="text-gray-500 text-xs mt-1">Nombre comercial: {tenant.name}</p>
          </div>

          {/* Country — rendered above Tax ID so the validator picks the right rules */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">País</label>
            <select
              value={formData.billingCountry}
              onChange={(e) => setFormData(prev => ({ ...prev, billingCountry: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-jarix-orange"
            >
              {countries.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Tax ID with live inline validation */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">
              {formData.billingCountry === 'UY' ? 'RUT' : formData.billingCountry === 'CL' ? 'RUT' : 'Identificador Fiscal'} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.taxId}
              onChange={(e) => setFormData(prev => ({ ...prev, taxId: e.target.value }))}
              className={`w-full bg-gray-700 border rounded-lg px-4 py-2 text-white focus:outline-none font-mono ${
                !taxIdTouched
                  ? 'border-gray-600 focus:border-jarix-orange'
                  : taxIdStatus.ok
                  ? 'border-green-500/60 focus:border-green-500'
                  : 'border-red-500/60 focus:border-red-500'
              }`}
              placeholder={formData.billingCountry === 'UY' ? '21.000.010.0013' : 'Identificador tributario'}
            />
            {/* Live feedback under the input — green check / red reason */}
            {taxIdTouched && (
              <div className="mt-1 flex items-center gap-2 text-xs">
                {taxIdStatus.ok ? (
                  <>
                    <span className="text-green-400">✓ Válido</span>
                    {taxIdStatus.formatted && (
                      <span className="text-gray-500 font-mono">({taxIdStatus.formatted})</span>
                    )}
                  </>
                ) : (
                  <span className="text-red-400">✗ {taxIdStatus.message}</span>
                )}
              </div>
            )}
            {formData.billingCountry === 'UY' && !taxIdTouched && (
              <p className="text-gray-500 text-xs mt-1">
                12 dígitos. Acepta puntos, guiones y espacios (se normaliza al guardar).
              </p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">Dirección de Facturación</label>
            <textarea
              value={formData.billingAddress}
              onChange={(e) => setFormData(prev => ({ ...prev, billingAddress: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-jarix-orange"
              rows={2}
              placeholder="Calle, número, oficina..."
            />
          </div>

          {/* Postal Code */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">Código Postal</label>
            <input
              type="text"
              value={formData.billingPostalCode}
              onChange={(e) => setFormData(prev => ({ ...prev, billingPostalCode: e.target.value }))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-jarix-orange"
              placeholder="ej: 11100"
            />
          </div>

          {/* Billing Emails */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">
              Emails de Facturación <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail() } }}
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-jarix-orange"
                placeholder="email@empresa.com"
              />
              <button
                type="button"
                onClick={handleAddEmail}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Agregar
              </button>
            </div>
            {formData.billingEmails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.billingEmails.map(email => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="text-gray-400 hover:text-red-400"
                      aria-label={`Eliminar ${email}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-gray-500 text-xs mt-1">Estos emails recibirán las facturas mensuales.</p>
          </div>

          {/* Sprint 2: Payment terms + billing day — the two values that drive
              the invoice lifecycle (due date derivation, cutoff day). Grouped
              in a subtle panel so the workflow context is visible but doesn't
              compete with the fiscal fields above. */}
          <div className="bg-gray-700/50 border border-gray-600/40 rounded-lg p-4 space-y-3">
            <h4 className="text-gray-300 text-sm font-semibold">Condiciones comerciales</h4>

            <div>
              <label className="block text-gray-400 text-xs font-medium mb-1">Condición de pago</label>
              <select
                value={formData.paymentTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value as BillingFormData['paymentTerms'] }))}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-jarix-orange"
              >
                {PAYMENT_TERMS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                ))}
              </select>
              <p className="text-gray-500 text-[11px] mt-1">
                La fecha de vencimiento se calcula automáticamente al marcar la factura como emitida.
              </p>
            </div>

            <div>
              <label className="block text-gray-400 text-xs font-medium mb-1">Día de corte</label>
              <input
                type="number"
                min={1}
                max={28}
                value={formData.billingDay}
                onChange={(e) => setFormData(prev => ({ ...prev, billingDay: parseInt(e.target.value || '15', 10) }))}
                className="w-24 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-jarix-orange"
              />
              <p className="text-gray-500 text-[11px] mt-1">
                Día del mes en que se captura el snapshot. Default global: 15.
              </p>
            </div>

            {/* Sprint 3 — pro-rata toggle. When enabled, users created
                inside the current billing period are charged proportionally
                to the days they were in the directory. Off by default
                because it's a commercial decision (some contracts stipulate
                full-month regardless of start date). */}
            <div className="border-t border-gray-600/40 pt-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.prorationEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, prorationEnabled: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-800 accent-jarix-orange"
                />
                <div className="flex-1">
                  <div className="text-gray-200 text-sm font-medium">Pro-rata para usuarios nuevos</div>
                  <div className="text-gray-500 text-[11px] mt-0.5">
                    Cobra proporcionalmente a los días en que un usuario creado mid-month estuvo en el directorio.
                    Ejemplo: user creado día 20 en un mes de 30 días → cobra 11/30 del precio mensual.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Plan info (read-only) */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-gray-400 text-sm font-medium mb-2">Información del Plan</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Plan:</span>
                <span className="ml-2 text-white font-medium">{tenant.planDisplayName || tenant.plan}</span>
              </div>
              <div>
                <span className="text-gray-500">Precio:</span>
                <span className="ml-2 text-white font-medium">${tenant.pricePerUser || 0}/usuario</span>
              </div>
              <div>
                <span className="text-gray-500">Usuarios:</span>
                <span className="ml-2 text-white font-medium">{tenant.billing?.billableUsers || 0}</span>
              </div>
              <div>
                <span className="text-gray-500">Total Mensual:</span>
                <span className="ml-2 text-green-400 font-bold">${(tenant.billing?.monthlyTotal || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !taxIdStatus.ok}
              className="bg-jarix-orange hover:bg-jarix-pink text-white font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title={!taxIdStatus.ok ? 'Corrige el identificador fiscal antes de guardar' : undefined}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Re-export for call sites that want to normalize before sending to the
// worker without importing the Uruguay lib directly.
export { normalizeRUT }
